import React, { useEffect, useState } from "react";
import { Container, InputContain, Span, Center } from "../GodMode";
import Button from "../../Generic/Button";
import InputBox from "../../Generic/InputBox";
import Card from "../../Generic/Card";
import ToggleSwitch from "../../Generic/ToggleSwitch";
import { errorNotification } from "../../Generic/Notifications";
import Loader from "../../Generic/Loader";
import EmptyState from "../../Generic/EmptyState";
import IotService from "../Services/iotService";
import { INPUT_TYPE } from "../../Generic/Forms/constants";
import Form from "../../Generic/Forms/Form";

const DispenserFormConfig = {
	section1: {
		fields: [
			{
				label: "Board ID",
				name: "boardId",
				required: true,
				type: INPUT_TYPE.NUMBER
			},
			{
				label: "System ID",
				name: "systemId",
				required: true,
				type: INPUT_TYPE.NUMBER,
			},
		],
	},
	section2: {
		fields: [
			{
				label: "Forward Rotation Degrees",
				name: "forwardRotationDegrees",
				required: false,
				type: INPUT_TYPE.NUMBER,
			},
			{
				label: "Forward Rotation RPM",
				name: "forwardRotationRpm",
				required: false,
				type: INPUT_TYPE.NUMBER,
			},
		],
	},
	section3: {
		fields: [
			{
				label: "Backward Rotation Degrees",
				name: "backwardRotationDegrees",
				required: false,
				type: INPUT_TYPE.NUMBER,
			},
			{
				label: "Backward Rotation RPM",
				name: "backwardRotationRpm",
				required: false,
				type: INPUT_TYPE.NUMBER,
			},
		],
	},
	section4: {
		fields: [
			{
				label: "Cycles",
				name: "cycles",
				required: false,
				type: INPUT_TYPE.NUMBER,
			},
			{
				label: "Delay between Operations (in ms)",
				name: "delayBetweenOperations",
				required: false,
				type: INPUT_TYPE.NUMBER,
			},
			{
				label: "Delay between Cycles (in ms)",
				name: "delayBetweenCycles",
				required: false,
				type: INPUT_TYPE.NUMBER,
			}
		],
	}
};

const DispenserTasks = {
	START_DISPENSE: "START_DISPENSE",
	STOP_DISPENSE: "STOP_DISPENSE",
	AGITATION: "AGITATION",
	PERIODIC_AGITATION_ON: "PERIODIC_AGITATION_ON",
	PERIODIC_AGITATION_OFF: "PERIODIC_AGITATION_OFF",
};

const DISPENSER_TASKS_CONFIG = {
	[DispenserTasks.START_DISPENSE]: { 
		taskType: "dispenser_test", 
		name: "Start Dispense Cycle" 
	},
	[DispenserTasks.STOP_DISPENSE]: { 
		taskType: "", 
		name: "Stop Dispense" 
	},
	[DispenserTasks.AGITATION]: { 
		taskType: "agitation", 
		name: "Start Agitation" 
	},
	[DispenserTasks.PERIODIC_AGITATION_ON]: { 
		taskType: "periodic_agitation_on", 
		name: "Turn On Periodic Agitation" 
	},
	[DispenserTasks.PERIODIC_AGITATION_OFF]: { 
		taskType: "periodic_agitation_off", 
		name: "Turn Off Periodic Agitation" 
	},
};

const Dispensers = ({ sendTask, kitchenId, loadingStates }) => {
	const [agitationPayload, setAgitationPayload] = useState({});
	const [periodicAgitations, setPeriodicAgitations] = useState([]);
	const [isLoadingPeriodicAgitations, setIsLoadingPeriodicAgitations] = useState(false);
	const [periodicAgitationsError, setPeriodicAgitationsError] = useState(null);

	const isStartDispenseLoading = loadingStates[DISPENSER_TASKS_CONFIG[DispenserTasks.START_DISPENSE].taskType] || false;
	const isAgitationLoading = loadingStates[DISPENSER_TASKS_CONFIG[DispenserTasks.AGITATION].taskType] || false;

	function updateAgitationPayload(event) {
		if (event) {
			setAgitationPayload((prev) => ({ ...prev, [event.target.id]: event.target?.valueAsNumber || event.target.value }));
		}
	}

	function startDispensing(payload) {
		//Add validation
		sendTask(DISPENSER_TASKS_CONFIG[DispenserTasks.START_DISPENSE], payload);
	}

	function startAgitation() {
		if (!agitationPayload.moduleId || !agitationPayload.systemId) {
			errorNotification("Module ID and System ID are required");
			return;
		}
		sendTask(DISPENSER_TASKS_CONFIG[DispenserTasks.AGITATION], agitationPayload);
	}

	useEffect(() => {
		fetchPeriodicAgitations();
	}, [kitchenId]);

	async function fetchPeriodicAgitations() {
		setIsLoadingPeriodicAgitations(true);
		setPeriodicAgitationsError(null);
		const { data, error } = await IotService.getPeriodicAgitations(kitchenId);
		if (data) {
			setPeriodicAgitations(data);
		} else if (error) {
			setPeriodicAgitationsError(error);
			errorNotification(error);
		}
		setIsLoadingPeriodicAgitations(false);
	}

	async function togglePeriodicAgitation(moduleId, systemId, currentStatus) {
		try {
			const taskType = currentStatus ? DispenserTasks.PERIODIC_AGITATION_OFF : DispenserTasks.PERIODIC_AGITATION_ON;
			await sendTask(
				DISPENSER_TASKS_CONFIG[taskType],
				{
					moduleId,
					systemId,
				}
			);
			return true;
		} catch (err) {
			errorNotification("Failed to toggle agitation status");
		}
		return false;
	}

	function renderPeriodicAgitations(modules, isLoading, error, onRetry) {
		if (isLoading) {
			return (
				<Center>
					<Loader />
				</Center>
			);
		}

		if (error) {
			return <EmptyState retry={onRetry} message={"Something went wrong while fetching periodic agitations"} />;
		}

		if (!modules.length) {
			return <EmptyState retry={onRetry} message="No periodic agitations configured" />;
		}

		return modules.map((agitation) => (
			<Card key={`${agitation.moduleId}-${agitation.systemId}`} padding="0">
				<ToggleSwitch
					title={`Module ${agitation.moduleId} - System ${agitation.systemId}`}
					initialStatus={agitation.isAgitation}
					onChange={(newStatus) => togglePeriodicAgitation(agitation.moduleId, agitation.systemId, agitation.isAgitation)}
					isAsync={true}
				/>
			</Card>
		));
	}

	return (
		<>
			<Card padding="0.5rem" alignItems="">
				<Form title="Dispense Cycle" config={DispenserFormConfig} submitButtonText="Start" onSubmit={startDispensing} canCancel={false} submitButtonLoading={isStartDispenseLoading} />
			</Card>

			<Container>
				<Card padding="1rem" alignItems="">
					<h2>Start Agitation</h2>
					<Container>
						<InputContain width="max-content">
							<Span padding="0 0 0.5rem 0.2rem" fontSize="1rem" fontWeight="600">
								Module ID
							</Span>
							<InputBox onChange={(e) => updateAgitationPayload(e)} type="number" required id="moduleId" />
						</InputContain>
						<InputContain width="max-content">
							<Span padding="0 0 0.5rem 0.2rem" fontSize="1rem" fontWeight="600">
								System ID
							</Span>
							<InputBox onChange={(e) => updateAgitationPayload(e)} type="number" required id="systemId" />
						</InputContain>
						<Button onClick={startAgitation} disabled={isAgitationLoading}>Start Agitation</Button>
					</Container>
				</Card>
			</Container>

			<Container>
				<Card padding="1rem" alignItems="" width="100%">
					<h2>Periodic Agitations</h2>
					<Container>
						{renderPeriodicAgitations(
							periodicAgitations,
							isLoadingPeriodicAgitations,
							periodicAgitationsError,
							fetchPeriodicAgitations
						)}
					</Container>
				</Card>
			</Container>
		</>
	);
};

export default Dispensers;
